import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { FaLink, FaTasks, FaShieldAlt, FaUser } from 'react-icons/fa';
import { clearTokens, getFreshAccessToken } from '../utils/authClient';
import { apiFetch } from '../utils/apiClient';
import ProjectLinksModal from '../pages/Client/components/ProjectLinksModal';
import AllTasksModal from '../pages/Client/components/AllTasksModal';
import SecurityModal from '../pages/Client/components/SecurityModal';
import { ProjectDataProvider, useProjectData } from '../pages/Client/context/ProjectDataContext';
import Tooltip from './Tooltip';

// Inner component that uses the context
const ClientPortalLayoutInner = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;
    const [showLinksModal, setShowLinksModal] = useState(false);
    const [showAllTasksModal, setShowAllTasksModal] = useState(false);
    const [showSecurityModal, setShowSecurityModal] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(null); // null = unknown
    const [mfaStatusFailed, setMfaStatusFailed] = useState(false);

    // MFA state for the header indicator. The status endpoint authenticates
    // with the Cognito ACCESS token, so refresh the session first - after an
    // overnight sleep the stored token is expired and would 401. A failed
    // check must surface as "unknown", never silently render as "off".
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const accessToken = await getFreshAccessToken();
                if (!accessToken) {
                    // No refreshable session - apiFetch will bounce the app to
                    // login on its next call; just mark the indicator unknown.
                    if (!cancelled) setMfaStatusFailed(true);
                    return;
                }
                const res = await fetch(`${process.env.REACT_APP_API_URL}/client/mfa/status`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                if (res.status === 401) {
                    // Freshly refreshed token still rejected - the session is
                    // genuinely dead. Re-login, matching apiFetch behaviour.
                    clearTokens();
                    window.location.href = '/client/login?expired=true';
                    return;
                }
                if (!res.ok) throw new Error(`MFA status returned ${res.status}`);
                const data = await res.json();
                if (!cancelled) {
                    setMfaEnabled(data.mfaEnabled === true);
                    setMfaStatusFailed(false);
                }
            } catch (err) {
                console.error('MFA status check failed:', err);
                if (!cancelled) setMfaStatusFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Get data from shared context
    const { project, lead, lastUpdate, blockedTasksMap } = useProjectData();

    // Derive display values from context data. The title dropped "Migration"
    // (D6, Bill 2026-08-21): with a company it reads "<company>'s Project",
    // without one just "Project".
    const companyPrefix = project && lead?.company
        ? `${lead.company}'s `
        : '';
    // Describe the actual migration (source -> target) rather than the
    // template the project was cloned from. Number of users is never shown.
    // Non-migration projects use the admin-entered Other description.
    // Generic line is the final resort for LEGACY rows only: approved
    // fallback (Bill, 2026-07-28), unreachable for rows created or edited
    // after the either/or rule shipped.
    const migrationDetail = lead?.sourceTenant && lead?.targetTenant
        ? `${lead.sourceTenant} → ${lead.targetTenant}`
        : null;
    const projectDescription = migrationDetail
        || lead?.projectDescription
        || 'Migration project managed by The Migration Experts';
    const userRole = lead?.clientRole || 'user';

    return (
        <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
        {/* This header stays mounted */}
        <div className="bg-white shadow sticky top-0 z-10 relative">
        {/* Full width, not max-w-7xl: the tabs sit hard left and the MFA/user
            group hard right, using the whole bar (Bill, 2026-07-31, trial) */}
        <div className="w-full px-4 py-4">
        <div className="flex justify-between items-center">
        {/* Left: Navigation Tabs */}
        <div className="flex gap-4">
        <button
        onClick={() => navigate('/client/project')}
        className={`pb-[5px] px-1 border-b-2 ${
            currentPath === '/client/project'
            ? 'text-primary-700 border-primary-700 font-medium'
            : 'text-gray-600 hover:text-primary-700 border-transparent'
        }`}
        >
        Kanban
        </button>
        <div className="h-6 w-px bg-gray-300 shrink-0 self-center" />
        <button
        onClick={() => navigate('/client/timeline')}
        className={`pb-[5px] px-1 border-b-2 ${
            currentPath === '/client/timeline'
            ? 'text-primary-700 border-primary-700 font-medium'
            : 'text-gray-600 hover:text-primary-700 border-transparent'
        }`}
        >
        Gantt
        </button>
        <div className="h-6 w-px bg-gray-300 shrink-0 self-center" />
        <button
        onClick={() => navigate('/client/risks')}
        className={`pb-[5px] px-1 border-b-2 ${
            currentPath === '/client/risks'
            ? 'text-primary-700 border-primary-700 font-medium'
            : 'text-gray-600 hover:text-primary-700 border-transparent'
        }`}
        >
        Risks/Issues
        </button>
        <div className="h-6 w-px bg-gray-300 shrink-0 self-center" />
        <button
        onClick={() => navigate('/client/wiki')}
        className={`pb-[5px] px-1 border-b-2 ${
            currentPath === '/client/wiki'
            ? 'text-primary-700 border-primary-700 font-medium'
            : 'text-gray-600 hover:text-primary-700 border-transparent'
        }`}
        >
        Wiki
        </button>
        <div className="h-6 w-px bg-gray-300 shrink-0 self-center" />
        <button
        onClick={() => navigate('/client/admin')}
        className={`pb-[5px] px-1 border-b-2 ${
            currentPath === '/client/admin'
            ? 'text-primary-700 border-primary-700 font-medium'
            : 'text-gray-600 hover:text-primary-700 border-transparent'
        }`}
        >
        {userRole === 'admin' ? 'Admin Settings' : 'Account Settings'}
        </button>
        </div>

        {/* Centre: project title, on the same row as the tabs so the header is
            one line instead of two (Bill, 2026-07-31, trial) */}
        <div className="flex-1 text-center px-4 min-w-0">
        <h1 className="text-2xl font-bold text-primary-900 truncate">
        {companyPrefix}<span className="text-red-900">Project</span>
        </h1>
        <p className="text-sm text-gray-600 truncate">{projectDescription}</p>
        </div>

        {/* Right: All Tasks + Project Links + MFA/user/Logout group */}
        <div className="flex items-center gap-2">
        {userRole === 'admin' && (
            <>
            <button
            onClick={() => setShowAllTasksModal(true)}
            className="text-sm px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
            >
            <FaTasks size={14} />
            All Tasks
            </button>
            <button
            onClick={() => setShowLinksModal(true)}
            className="text-sm px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
            >
            <FaLink size={14} />
            Project Links
            </button>
            </>
        )}
        <div className="flex items-center gap-2">
        <Tooltip text={mfaEnabled === null
            ? (mfaStatusFailed ? 'Could not verify MFA status - click to retry' : 'Checking MFA status…')
            : mfaEnabled ? 'Multi-factor authentication is on' : 'Multi-factor authentication is off'}>
        <button
        onClick={() => setShowSecurityModal(true)}
        className="text-sm px-4 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 flex items-center gap-2"
        >
        {/* Unknown must never look like "off": amber when the check failed */}
        <FaShieldAlt size={14} className={
            mfaEnabled === null
                ? (mfaStatusFailed ? 'text-amber-500' : 'text-gray-300')
                : mfaEnabled ? 'text-green-600' : 'text-gray-400'
        } />
        {mfaEnabled === null ? (mfaStatusFailed ? 'MFA ?' : 'MFA') : mfaEnabled ? 'MFA on' : 'MFA off'}
        </button>
        </Tooltip>
        {lead?.email && (
            <span className="text-sm text-gray-600 flex items-center gap-2">
            <FaUser size={12} />
            {lead.email}
            </span>
        )}
        {/* Separator immediately left of Logout (Bill, 2026-07-31) */}
        <div className="h-6 w-px bg-gray-300 mx-1 shrink-0" />
        <button
        onClick={() => {
            clearTokens();
            navigate('/');
        }}
        className="btn-primary text-sm px-4 py-2"
        >
        Logout
        </button>
        </div>
        </div>
        </div>
        </div>
        </div>


        {/* This renders Dashboard or Wiki without unmounting the header */}
        <div className="flex-1 overflow-hidden">
        <Outlet />
        </div>

        {/* Project Links Modal */}
        <ProjectLinksModal
            isOpen={showLinksModal}
            onClose={() => setShowLinksModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            apiFetch={apiFetch}
        />

        {/* All Tasks Modal */}
        <AllTasksModal
            isOpen={showAllTasksModal}
            onClose={() => setShowAllTasksModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            apiFetch={apiFetch}
            refreshKey={lastUpdate}
            blockedTasksMap={blockedTasksMap}
        />

        {/* MFA Modal */}
        <SecurityModal
            isOpen={showSecurityModal}
            onClose={() => setShowSecurityModal(false)}
            apiUrl={process.env.REACT_APP_API_URL}
            onStatusChange={(enabled) => { setMfaEnabled(enabled); setMfaStatusFailed(false); }}
        />
        </div>
    );
};

// Wrapper that provides the context
const ClientPortalLayout = () => {
    return (
        <ProjectDataProvider>
            <ClientPortalLayoutInner />
        </ProjectDataProvider>
    );
};

export default ClientPortalLayout;
